<script setup lang="ts">
import {computed, onMounted, ref, watch} from 'vue';
import {listObjects} from '@/base/callBackend';
import {CabKPI, CabOrganization, CabReportItem, renderRI_debug, StatusEnum, uuidOf} from 'common';
import {ElButton, ElCollapseTransition, ElSkeleton, ElSkeletonItem} from 'element-plus';
import AdminReportItemKpiMijlpaal from '@/views/admin/AdminReportItemKpiMijlpaal.vue';
import ReportItemFilters from '@/views/shared/ReportItemFilters.vue';
import {EVENT_BUS} from '@/base/EVENT_BUS.js';
import RedGreenMarker from '@/views/general/RedGreenMarker.vue';
import ExpandButton from '@/views/general/ExpandButton.vue';
import {localStorageGetJson, localStorageSetJson} from '@/base/utils.js';

type KpiGroup = {
    kpi: CabKPI,
    reportItems: CabReportItem[],
}

const allOrganizations = ref<CabOrganization[]>([]);
const allRaw           = ref<CabReportItem[]>([]); // Raw data from backend (all statuses)
const filtered         = ref<CabReportItem[]>([]); // after all filters
const loading          = ref<boolean>(true);
const filtersExpanded  = ref<boolean>(localStorageGetJson('admin:filtersExpanded') ?? true);

watch(filtersExpanded, newVal => localStorageSetJson('admin:filtersExpanded', newVal));

// Only KPIs that have at least one item left after filtering, in the order the items appear
const kpiGroups   = computed<KpiGroup[]>(() => {
    const groups = new Map<string, KpiGroup>();
    for (const ri of filtered.value) {
        const kpi   = ri.kpi[0];
        const group = groups.get(uuidOf(kpi));
        if (group) {
            group.reportItems.push(ri);
        } else {
            groups.set(uuidOf(kpi), {
                kpi,
                reportItems: [ri],
            });
        }
    }
    return Array.from(groups.values());
});
// items of several instellingen/themas can end up under one KPI, then they need to say which one they are
const showOrgTheme = computed<boolean>(() =>
    new Set(filtered.value.map(ri => ri.organization[0].uuid)).size > 1 ||
    new Set(filtered.value.map(ri => ri.theme[0].uuid)).size > 1,
);

onMounted(async () => {
    allOrganizations.value = await listObjects(CabOrganization);
    const all              = await listObjects(CabReportItem);
    for (const ri of all) {
        if (ri.status !== StatusEnum.future) {
            if (ri.kpi[0].waardeExpression && !ri.waardeExpressionTraces) {
                console.log(`TOMTOMTOM waardeExpression  maar geen trace ${renderRI_debug(ri)}`);
            }
            if (ri.kpi[0].gehaaldExpression && !ri.gehaaldExpressionTraces) {
                console.log(`TOMTOMTOM gehaaldExpression maar geen trace ${renderRI_debug(ri)}`);
            }
        }
    }
    allRaw.value  = all.sort((a, b) => a.compare(b));
    loading.value = false;
});

function goToTop() {
    document.getElementById('app')?.scrollTo({
        top     : 0,
        behavior: 'smooth',
    });
}

function sendEvent(msg: 'red' | 'green') {
    EVENT_BUS[msg] = !EVENT_BUS[msg];
}
</script>

<template>
    <el-button size="large"
               @click="goToTop"
               class="fixed bottom-3 left-3 w-14 h-14 rounded text-4xl"
    >🔝
    </el-button>

    <div class="flex flex-col mx-28 my-5">
        <div class="flex flex-row justify-end pr-10">
            <RedGreenMarker :show-green-marker="true"
                            :show-red-marker="true"
                            v-on:red="sendEvent('red')"
                            v-on:green="sendEvent('green')"
            />
        </div>

        <div class="flex flex-row items-center px-10">
            <ExpandButton v-model="filtersExpanded"/>
            <span class="font-thin">filters</span>
        </div>
        <el-collapse-transition>
            <!-- v-show (not v-if): keep the filters mounted while collapsed so they keep emitting update:filtered -->
            <div v-show="filtersExpanded">
                <ReportItemFilters v-if="!loading"
                                   :all="allRaw"
                                   storage-key-prefix="admin"
                                   :show-reporter-filter="true"
                                   :show-status-filter="true"
                                   @update:filtered="filtered = $event"
                />
            </div>
        </el-collapse-transition>

        <div>
            <template v-if="loading">
                <el-skeleton animated :count="3">
                    <template #template>
                        <el-skeleton-item class="h-44"/>
                    </template>
                </el-skeleton>
            </template>
            <template v-else>

                <AdminReportItemKpiMijlpaal
                    v-for="group in kpiGroups"
                    :key="uuidOf(group.kpi)"
                    :kpi="group.kpi"
                    :reportitems="group.reportItems"
                    :show-org-theme="showOrgTheme"
                    :all-organizations="allOrganizations"
                />

            </template>
        </div>
    </div>
</template>
